import { ApiResponse } from '../types';
import { AsyncMutex } from '../utils/AsyncMutex';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Resolve API base URL from multiple env sources (CRA, Vite, runtime)
function resolveApiBaseUrl(): string {
  const fromCra = process.env.REACT_APP_API_URL as string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fromVite = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_API_URL) as string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fromWindow = (typeof window !== 'undefined' && (window as any).__API_URL__) as string | undefined;
  const url = fromCra || fromVite || fromWindow || 'https://localhost:5001';
  return url.replace(/\/$/, '');
}

export const API_BASE_URL = resolveApiBaseUrl();

class ApiService {
  private baseURL: string;
  private mutex = new AsyncMutex();
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    signal?: AbortSignal,
    _isRetry = false // Add retry flag
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const defaultHeaders: Record<string, string> = {};

    const method = options.method?.toUpperCase() || 'GET';
    // Only set JSON content type if it's not FormData
    if (method !== 'GET' && method !== 'DELETE' && !(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Always get fresh token from storage
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      signal: signal || options.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    const doFetch = async (): Promise<Response> => fetch(url, config);

    try {
      let response = await doFetch();

      // Handle 401
      if (response.status === 401 && !_isRetry) {
        // Use mutex to ensure only one refresh happens
        // But optimization: check if we can join an existing promise first
        const refreshed = await this.refreshTokenSafely();

        if (refreshed) {
          // Update token in headers
          const newToken = localStorage.getItem(TOKEN_KEY);
          if (newToken) {
            // Cast to any/Record to fix type issue
            (config.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
          }
          // Retry request
          return this.request<T>(endpoint, options, signal, true);
        }
      }

      // Response body checks...
      const contentType = response.headers.get('content-type');
      const text = await response.text();

      let data: any;
      if (text && text.trim().length > 0) {
        if (contentType?.includes('application/json')) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError, 'Response text:', text);
            return {
              success: false,
              message: 'Sunucudan geçersiz yanıt alındı. Lütfen tekrar deneyin.',
              error: text.substring(0, 200)
            } as ApiResponse<T>;
          }
        } else {
          // Treat non-JSON text responses as error messages unless strictly handled otherwise
          throw new Error(text || 'Bir hata oluştu');
        }
      } else {
        if (!response.ok) {
          return {
            success: false,
            message: `Sunucu hatası: ${response.status} ${response.statusText}`,
            error: `HTTP ${response.status}`
          } as ApiResponse<T>;
        }
        data = {};
      }

      if (!response.ok) {
        const errorMessage = data?.message ||
          data?.Message ||
          data?.errorCode ||
          data?.ErrorCode ||
          data?.errors?.[0] ||
          text ||
          'Bir hata oluştu';

        const errorCode = data?.errorCode || data?.ErrorCode;
        if (response.status === 403 && errorCode === 'TERMS_NOT_ACCEPTED') {
          if (typeof window !== 'undefined' &&
            !window.location.pathname.includes('/accept-terms') &&
            !window.location.pathname.includes('/login')) {
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = `/accept-terms?returnUrl=${returnUrl}`;
          }
        }

        return {
          success: false,
          message: errorMessage,
          error: data
        } as ApiResponse<T>;
      }

      if (data && typeof data === 'object' && 'success' in data) {
        return data;
      } else if (data && typeof data === 'object' && ('Success' in data || 'success' in data)) {
        const successValue = (data as any).Success ?? (data as any).success;
        return {
          success: successValue === true,
          data: data
        } as ApiResponse<T>;
      } else {
        return {
          success: true,
          data: data
        } as ApiResponse<T>;
      }

    } catch (error) {
      if (error instanceof Error && (error.name === 'AbortError' || error.message?.includes('aborted'))) {
        return {
          success: false,
          message: 'İstek iptal edildi'
        } as ApiResponse<T>;
      }
      console.error('API Error:', error);
      throw error;
    }
  }

  // PUBLIC HELPER METHODS

  public get<T>(endpoint: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' }, signal);
  }

  public post<T>(endpoint: string, body: any = {}, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    }, signal);
  }

  public put<T>(endpoint: string, body: any = {}, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    }, signal);
  }

  public patch<T>(endpoint: string, body: any = {}, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }, signal);
  }

  public delete<T>(endpoint: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' }, signal);
  }

  public postFormData<T>(endpoint: string, formData: FormData, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData
    }, signal);
  }

  public putFormData<T>(endpoint: string, formData: FormData, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: formData
    }, signal);
  }

  public async uploadFile<T>(endpoint: string, file: File, signal?: AbortSignal): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.postFormData<T>(endpoint, formData, signal);
  }

  public async uploadFileWithFormData<T>(endpoint: string, formData: FormData, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.postFormData<T>(endpoint, formData, signal);
  }

  // New Safe Refresh Method
  public async refreshTokenSafely(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    return this.mutex.run(async () => {
      // Re-check promise in case it was created while waiting for mutex
      if (this.refreshPromise) {
        return this.refreshPromise;
      }

      this.refreshPromise = this.tryRefreshToken();
      try {
        const result = await this.refreshPromise;
        return result;
      } finally {
        this.refreshPromise = null;
      }
    });
  }

  // Raw refresh logic
  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      const payload = data?.data ?? data;

      if (res.ok && payload?.token) {
        const { token, refreshToken: newRefresh, expiresIn } = payload;
        localStorage.setItem(TOKEN_KEY, token);
        if (newRefresh) {
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);
        }
        const expirySeconds = expiresIn || 3600;
        localStorage.setItem(TOKEN_EXPIRY_KEY, (Date.now() + expirySeconds * 1000).toString());
        return true;
      }
    } catch { }

    // Fallback cleanup
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    return false;
  }
}

export const apiService = new ApiService();
export default apiService;
